<template>
  <component
    :is="tag"
    :id="id"
    class="base-headline"
    :class="[{ flush: flush }, align && `align-${align}`, size && `size-${size}`]"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';

/**
 * Semantic headline component that renders appropriate heading tags
 *
 * Dynamically renders h1-h6 tags based on level prop while maintaining
 * consistent styling. Useful for maintaining semantic HTML structure.
 *
 * @example Default h2 heading
 * ```vue
 * <BaseHeadline>Section Title</BaseHeadline>
 * ```
 *
 * @example Page title (h1)
 * ```vue
 * <BaseHeadline :level="1">Page Title</BaseHeadline>
 * ```
 *
 * @example Flush heading (no bottom margin)
 * ```vue
 * <BaseHeadline :level="3" flush>
 *   Compact heading with no spacing below
 * </BaseHeadline>
 * ```
 *
 * @example Correct outline position, independent appearance
 * ```vue
 * <!-- second level of the document, sized like a minor heading -->
 * <BaseHeadline :level="2" size="lg">Section title</BaseHeadline>
 * ```
 */
export interface BaseHeadlineProps {
  /**
   * Heading level (1-6) determining which HTML tag to render
   * - 1: Renders h1 tag (page title)
   * - 2-6: Renders corresponding h2-h6 tags
   * @default 2
   */
  level?: number;

  /**
   * Font size, decoupled from `level` so a heading can sit at the correct place in
   * the document outline without dictating how large it looks. Omit to keep the
   * browser's default size for the rendered tag.
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Removes bottom margin (useful when heading is last child)
   * @default false
   */
  flush?: boolean;

  /**
   * Text alignment
   * @default undefined
   */
  align?: 'center' | 'end' | 'start';

  /** Unique element ID for anchor linking */
  id?: string;
}

const props = withDefaults(defineProps<BaseHeadlineProps>(), {
  level: 2,
  flush: false,
});

const tag = computed(() => {
  return `h${props.level}`;
});
</script>

<style>
.base-headline {
  &:is(.flush, :only-child) {
    margin-bottom: 0;
  }

  &.align-center {
    text-align: center;
  }

  &.align-end {
    text-align: right;
  }

  &:is(h1) {
    font-family: var(--font-display);
    font-weight: var(--heading-1-weight, var(--font-normal));
    margin-bottom: var(--gap-sm);
  }
  &:is(h2, h3, h4, h5, h6) {
    font-weight: var(--heading-n-weight, var(--font-bold));
    font-family: var(--font-body);
    margin-bottom: var(--gap-sm);
  }

  /* Opt-in only — without a size the rendered tag keeps its browser default, so
     existing call sites are unaffected. */
  &.size-xs {
    font-size: var(--font-xs);
  }
  &.size-sm {
    font-size: var(--font-sm);
  }
  &.size-md {
    font-size: var(--font-md);
  }
  &.size-lg {
    font-size: var(--font-lg);
  }
  &.size-xl {
    font-size: var(--font-xl);
  }
  small {
    display: block;
    font-size: 0.8em;
    margin-top: var(--gap-xs);
  }
}
</style>
